import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Beaker, Plus, Trash2, RefreshCw, Save } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button, Input, TextArea } from "../../components/common/Primitives";
import api from "../../lib/api";

function VariantEditor({ v, onChange, onRemove, canRemove, idx }) {
  return (
    <div className="border border-navy/15 rounded-lg p-4 bg-white/70 space-y-3" data-testid={`hero-variant-${v.id || idx}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-widest text-[#8A6600]">Variant #{idx + 1}</div>
        {canRemove && (
          <button type="button" onClick={onRemove} className="text-rose-600 text-xs flex items-center gap-1 hover:underline" data-testid={`hero-variant-remove-${idx}`}>
            <Trash2 size={12} /> Remove
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Variant ID (short slug)" value={v.id} onChange={(e) => onChange({ ...v, id: e.target.value.replace(/\s+/g, "-").toLowerCase() })} testId={`hero-variant-id-${idx}`} />
        <Input label="Traffic weight (%)" type="number" min="0" max="100" value={v.weight_pct} onChange={(e) => onChange({ ...v, weight_pct: parseInt(e.target.value || "0", 10) })} testId={`hero-variant-weight-${idx}`} />
      </div>
      <Input label="Headline" value={v.headline} onChange={(e) => onChange({ ...v, headline: e.target.value })} testId={`hero-variant-headline-${idx}`} />
      <TextArea label="Subheadline" rows={3} value={v.subheadline} onChange={(e) => onChange({ ...v, subheadline: e.target.value })} testId={`hero-variant-sub-${idx}`} />
      <Input label="CTA button label" value={v.cta_label} onChange={(e) => onChange({ ...v, cta_label: e.target.value })} testId={`hero-variant-cta-${idx}`} />
    </div>
  );
}

export default function HeroSplitTest() {
  const [config, setConfig] = useState({ enabled: false, variants: [] });
  const [stats, setStats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/hero-split-test");
      setConfig(data.config);
      setStats(data.stats || []);
    } catch { toast.error("Failed to load config"); }
  };

  useEffect(() => { load(); }, []);

  const totalWeight = (config.variants || []).reduce((s, v) => s + (parseInt(v.weight_pct || 0, 10) || 0), 0);

  const updateVariant = (idx, next) => {
    const arr = [...(config.variants || [])];
    arr[idx] = next;
    setConfig({ ...config, variants: arr });
  };

  const removeVariant = (idx) => {
    const arr = (config.variants || []).filter((_, i) => i !== idx);
    setConfig({ ...config, variants: arr });
  };

  const addVariant = () => {
    if ((config.variants || []).length >= 6) { toast.error("Max 6 variants"); return; }
    const arr = [...(config.variants || []),
      { id: `variant-${(config.variants?.length || 0) + 1}`, weight_pct: 0,
        headline: "New headline copy…", subheadline: "New subheadline copy…",
        cta_label: "Apply for Funding" }];
    setConfig({ ...config, variants: arr });
  };

  const save = async () => {
    if (totalWeight !== 100) { toast.error(`Weights must total 100% (currently ${totalWeight}%)`); return; }
    setSaving(true);
    try {
      await api.put("/admin/hero-split-test", config);
      toast.success("Split-test config saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetStats = async () => {
    if (!window.confirm("Reset ALL impressions and conversions across every variant?")) return;
    setResetting(true);
    try {
      await api.post("/admin/hero-split-test/reset-stats");
      toast.success("Stats reset");
      await load();
    } catch { toast.error("Reset failed"); }
    finally { setResetting(false); }
  };

  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl text-ink flex items-center gap-2">
              <Beaker size={22} className="text-gold" /> Hero Copy Split-Test
            </h1>
            <div className="text-xs text-ink-soft mt-1 max-w-2xl">
              Live A/B on the landing hero. Weight-split traffic across variants and see which headline converts more signups.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-ink flex items-center gap-2 cursor-pointer" data-testid="hero-split-enabled-row">
              <input type="checkbox" checked={!!config.enabled} onChange={(e) => setConfig({ ...config, enabled: e.target.checked })} className="accent-gold h-4 w-4" data-testid="hero-split-enabled" />
              <span className={`text-xs font-mono uppercase tracking-widest ${config.enabled ? "text-emerald-700" : "text-ink-soft"}`}>
                {config.enabled ? "LIVE" : "DISABLED"}
              </span>
            </label>
          </div>
        </div>

        {/* Stats table */}
        <Card testId="hero-split-stats">
          <div className="overline mb-3">Live Analytics</div>
          {stats.length === 0 ? (
            <div className="text-sm text-ink-soft italic">No traffic recorded yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-mono uppercase tracking-widest text-ink-soft border-b border-navy/10">
                    <th className="text-left px-3 py-2">Variant</th>
                    <th className="text-right px-3 py-2">Impressions</th>
                    <th className="text-right px-3 py-2">Signups</th>
                    <th className="text-right px-3 py-2">CR%</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((s) => (
                    <tr key={s.variant_id} className="border-b border-navy/5" data-testid={`hero-split-stat-${s.variant_id}`}>
                      <td className="px-3 py-2 font-mono">{s.variant_id}</td>
                      <td className="px-3 py-2 text-right">{s.impressions.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">{s.signups.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">
                        <Pill tone={s.conversion_rate_pct >= 5 ? "success" : "muted"}>
                          {s.conversion_rate_pct.toFixed(2)}%
                        </Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3">
            <Button variant="secondary" onClick={resetStats} disabled={resetting} testId="hero-split-reset-stats">
              <RefreshCw size={12} className="inline mr-1" />{resetting ? "Resetting…" : "Reset stats"}
            </Button>
          </div>
        </Card>

        {/* Variant editor */}
        <Card testId="hero-split-editor">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Variants</div>
            <div className={`text-xs font-mono uppercase tracking-widest ${totalWeight === 100 ? "text-emerald-700" : "text-rose-700"}`} data-testid="hero-split-total-weight">
              Total weight: {totalWeight}% {totalWeight === 100 ? "✓" : "(must equal 100)"}
            </div>
          </div>
          <div className="space-y-4">
            {(config.variants || []).map((v, i) => (
              <VariantEditor
                key={i} v={v} idx={i}
                canRemove={(config.variants || []).length > 1}
                onChange={(next) => updateVariant(i, next)}
                onRemove={() => removeVariant(i)}
              />
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button variant="secondary" onClick={addVariant} testId="hero-split-add-variant">
              <Plus size={12} className="inline mr-1" /> Add variant
            </Button>
            <Button onClick={save} disabled={saving || totalWeight !== 100} testId="hero-split-save">
              <Save size={12} className="inline mr-1" />{saving ? "Saving…" : "Save & apply"}
            </Button>
          </div>
        </Card>
      </div>
    </DashboardShell>
  );
}
